import { useState, useEffect, useRef, Fragment } from "react";
import { SelectField } from "../components/ui/select-field.js";
import { api, getAccessToken } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { DatePicker } from "../components/DatePicker.js";
import { Progress } from "../components/ui/progress.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { ADMIN_ROLES, HR_ROLES } from "@hrms/types";
import { CheckCircle2, AlertTriangle, XCircle, Search, HelpCircle, AlertCircle, Flag } from "lucide-react";
import { SpecularButton } from "../components/ui/specular-button.js";
import { Pagination } from "../components/ui/pagination.js";
import { usePageBack } from "../context/PageBackContext.js";
import { TimeInput } from "../components/ui/time-input.js";

const today = new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

function decodeRole(): string {
  try {
    const t = getAccessToken() ?? "";
    return String((JSON.parse(atob(t.split(".")[1]!)) as Record<string, unknown>)["role"] ?? "");
  } catch { return ""; }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface AttendanceSource {
  id: string; name: string; officeId: string | null; sourceFormat: string;
  deviceSerial: string | null; lastWatermark: number;
  lastFileAt: string | null; lastIngestAt: string | null; createdAt: string;
  office: { id: string; name: string; city: string } | null;
  _count: { employees: number; rawLogs: number; runs: number };
}
interface SourceEmployee {
  id: string; externalCode: string; externalName: string; employeeId: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string } | null;
}
interface Suggestion {
  sourceEmployeeId: string; externalCode: string; externalName: string;
  suggestedEmployee: { id: string; firstName: string; lastName: string; employeeCode: string };
}
interface RawLog { id: string; externalLogId: number; externalEmpCode: string; logDateTime: string; direction: string | null; deviceId: number | null }
interface Stats {
  lastWatermark: number; lastFileAt: string | null; lastIngestAt: string | null;
  totalLogs: number; totalEmployees: number; mappedEmployees: number; unmappedEmployees: number; unmappedLogs: number;
  dateRange: { min: string | null; max: string | null };
  uploadStatus: string | null; uploadStartedAt: string | null; uploadError: string | null;
  uploadEmployeesSynced: number | null; uploadLogsAdded: number | null; uploadComputedAdded: number | null; uploadSkipped: number | null;
  uploadProgressCurrent: number | null; uploadProgressTotal: number | null; uploadProgressNote: string | null;
}
interface DailyRow { date: string; present: number; absent: number; singlePunch: number }
interface EmpPatternRow { externalCode: string; displayName: string; daysWorked: number; singlePunchDays: number; avgInTime: string | null; avgOutTime: string | null }
interface LateRow { externalCode: string; displayName: string; lateDays: number; avgLateTime: string | null }
interface IngestionRun { id: string; status: string; periodLabel: string | null; rawLogsImported: number; attendanceCreated: number; attendanceUpdated: number; unmatchedCount: number; flaggedForReview: number; startedAt: string; completedAt: string | null }
interface HRMSEmployee { id: string; firstName: string; lastName: string; employeeCode: string }

// ─── Shared styles ────────────────────────────────────────────────────────────

const card: React.CSSProperties = { background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "16px" };
const btnPrimary: React.CSSProperties = { padding: "8px 14px", borderRadius: "var(--radius-md)", fontSize: "13px", fontWeight: 600, cursor: "pointer", border: "none", background: "var(--color-brand-600)", color: "#fff" };
const btnSecondary: React.CSSProperties = { padding: "7px 13px", borderRadius: "var(--radius-md)", fontSize: "13px", fontWeight: 500, cursor: "pointer", border: "1px solid var(--color-surface-200)", background: "var(--color-surface-0)", color: "var(--color-surface-900)" };
const inputStyle: React.CSSProperties = { padding: "7px 10px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: "13px", background: "var(--color-surface-0)", width: "100%", boxSizing: "border-box" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "13px" };
const th: React.CSSProperties = { padding: "7px 10px", textAlign: "left", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--color-surface-200)", color: "var(--color-surface-500)" };
const td: React.CSSProperties = { padding: "7px 10px", borderBottom: "1px solid var(--color-surface-100)", verticalAlign: "middle" };
const chip = (color: string, bg: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 8px", borderRadius: "9999px", fontSize: "11px", fontWeight: 600, color, background: bg });
const statBlock: React.CSSProperties = { padding: "12px 16px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", textAlign: "center" };

function fmtDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// ─── Main page ────────────────────────────────────────────────────────────────

type DetailTab = "overview" | "employees" | "logs" | "runs";
type LogSubTab = "table" | "daily" | "patterns" | "late";

export default function AttendanceSourcesPage() {
  usePageBack("/admin", "Back to Admin");
  const role      = decodeRole();
  const isAdmin   = ADMIN_ROLES.has(role);
  const canView   = HR_ROLES.has(role);

  const [sources, setSources]         = useState<AttendanceSource[]>([]);
  const [selected, setSelected]       = useState<AttendanceSource | null>(null);
  const [detailTab, setDetailTab]     = useState<DetailTab>("overview");
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm]         = useState({ name: "", officeId: "", deviceSerial: "", sourceFormat: "ETIMETRACK_MDB" });

  const load = async () => {
    if (!canView) return;
    try {
      const { data } = await api.get<AttendanceSource[]>("/admin/attendance-sources");
      setSources(data);
    } catch (err) { logError("AttendanceSourcesPage.load", err); }
  };

  const handleDelete = async (src: AttendanceSource, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${src.name}"?\n\nThis will permanently remove all ${src._count.rawLogs.toLocaleString()} logs and ${src._count.employees} employees linked to this source. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/attendance-sources/${src.id}`);
      setSuccess(`"${src.name}" deleted.`);
      void load();
    } catch (err) { logError("AttendanceSourcesPage.handleDelete", err); setError("Delete failed."); }
  };

  useEffect(() => { void load(); }, []);

  const handleAdd = async () => {
    if (!addForm.name) { setError("Name is required"); throw new Error("validation"); }
    setLoading(true); setError("");
    try {
      const { data } = await api.post<AttendanceSource>("/admin/attendance-sources", {
        name:         addForm.name,
        officeId:     addForm.officeId     || null,
        deviceSerial: addForm.deviceSerial || null,
        sourceFormat: addForm.sourceFormat,
      });
      setSources(prev => [...prev, data]);
      setAddForm({ name: "", officeId: "", deviceSerial: "", sourceFormat: "ETIMETRACK_MDB" });
      setShowAddForm(false);
      setSuccess("Source created");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      logError("AttendanceSourcesPage.handleAdd", err);
      setError("Failed to create source");
      throw err;
    } finally { setLoading(false); }
  };

  if (!canView) return (
    <div style={{ padding: "40px", textAlign: "center", color: "var(--color-surface-400)" }}>
      Access restricted.
    </div>
  );

  if (selected) {
    return (
      <DetailView
        source={selected}
        isAdmin={isAdmin}
        detailTab={detailTab}
        setDetailTab={setDetailTab}
        onBack={() => { setSelected(null); void load(); }}
      />
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Attendance Sources</h1>
          <p style={{ fontSize: "13px", color: "var(--color-surface-500)", margin: "2px 0 0" }}>
            Biometric device feeds — import, explore, and ingest punch logs
          </p>
        </div>
        {isAdmin && (
          <button style={btnPrimary} onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? "Cancel" : "+ Add Source"}
          </button>
        )}
      </div>

      {error   && <div style={{ marginBottom: "12px", padding: "10px 14px", background: "var(--color-error-bg-subtle)", border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-md)", fontSize: "13px", color: "var(--color-error-fg)" }}>{error}</div>}
      {success && <div style={{ marginBottom: "12px", padding: "10px 14px", background: "var(--color-success-bg-subtle)", border: "1px solid var(--color-success-border)", borderRadius: "var(--radius-md)", fontSize: "13px", color: "var(--color-success-fg)" }}>{success}</div>}

      {showAddForm && (
        <div style={{ ...card, marginBottom: "20px" }}>
          <h3 style={{ fontSize: "14px", fontWeight: 600, margin: "0 0 12px" }}>New Attendance Source</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-600)" }}>Name *</label>
              <input style={{ ...inputStyle, marginTop: "4px" }} placeholder="e.g. Delhi Office" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-600)" }}>Device Serial</label>
              <input style={{ ...inputStyle, marginTop: "4px" }} placeholder="from Devices table" value={addForm.deviceSerial} onChange={e => setAddForm(f => ({ ...f, deviceSerial: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-600)" }}>Format</label>
              <SelectField className="mt-1" value={addForm.sourceFormat} onChange={e => setAddForm(f => ({ ...f, sourceFormat: e.target.value }))}>
                <option value="ETIMETRACK_MDB">eTimeTrackLite MDB</option>
              </SelectField>
            </div>
          </div>
          <AsyncButton onClick={() => handleAdd()} disabled={loading}>Create Source</AsyncButton>
        </div>
      )}

      {sources.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: "40px", color: "var(--color-surface-400)" }}>
          No attendance sources yet. Add one to get started.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px" }}>
          {sources.map(src => (
            <div key={src.id} style={{ ...card, cursor: "pointer" }} onClick={() => { setSelected(src); setDetailTab("overview"); }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px" }}>{src.name}</div>
                  <div style={{ fontSize: "12px", color: "var(--color-surface-500)", marginTop: "2px" }}>
                    {src.office ? `${src.office.name}, ${src.office.city}` : "No office linked"} · {src.sourceFormat}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={chip("var(--color-info-fg)", "var(--color-info-bg)")}>{src.sourceFormat.replace("_", " ")}</span>
                  {isAdmin && (
                    <button
                      onClick={e => handleDelete(src, e)}
                      title="Delete source and all its data"
                      style={{ background: "none", border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-sm)", color: "var(--color-error)", fontSize: "11px", padding: "2px 8px", cursor: "pointer" }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "10px" }}>
                <div style={statBlock}>
                  <div style={{ fontSize: "18px", fontWeight: 700 }}>{src._count.rawLogs.toLocaleString()}</div>
                  <div style={{ fontSize: "11px", color: "var(--color-surface-500)" }}>Raw Logs</div>
                </div>
                <div style={statBlock}>
                  <div style={{ fontSize: "18px", fontWeight: 700 }}>{src._count.employees}</div>
                  <div style={{ fontSize: "11px", color: "var(--color-surface-500)" }}>Employees</div>
                </div>
                <div style={statBlock}>
                  <div style={{ fontSize: "18px", fontWeight: 700 }}>{src._count.runs}</div>
                  <div style={{ fontSize: "11px", color: "var(--color-surface-500)" }}>Ingest Runs</div>
                </div>
              </div>
              <div style={{ fontSize: "12px", color: "var(--color-surface-500)", display: "flex", gap: "12px" }}>
                <span>Last file: {fmtDateTime(src.lastFileAt)}</span>
                <span>Last ingest: {fmtDateTime(src.lastIngestAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function DetailView({ source, isAdmin, detailTab, setDetailTab, onBack }: {
  source: AttendanceSource; isAdmin: boolean;
  detailTab: DetailTab; setDetailTab: (t: DetailTab) => void;
  onBack: () => void;
}) {
  const tabs: { key: DetailTab; label: string }[] = [
    { key: "overview",  label: "Overview"  },
    { key: "employees", label: "Employees" },
    { key: "logs",      label: "Raw Logs"  },
    { key: "runs",      label: "Runs"      },
  ];

  return (
    <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
        <SpecularButton onClick={onBack}>← Back</SpecularButton>
        <div>
          <h1 style={{ fontSize: "18px", fontWeight: 700, margin: 0 }}>{source.name}</h1>
          <div style={{ fontSize: "12px", color: "var(--color-surface-500)" }}>
            {source.office ? `${source.office.name}, ${source.office.city}` : "No office"} · {source.sourceFormat}
            {source.deviceSerial ? ` · Device ${source.deviceSerial}` : ""}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: "4px", borderBottom: "1px solid var(--color-surface-200)", marginBottom: "20px" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setDetailTab(t.key)} style={{
            padding: "8px 16px", border: "none", borderBottom: detailTab === t.key ? "2px solid var(--color-brand-600)" : "2px solid transparent",
            background: "transparent", cursor: "pointer", fontSize: "13px", fontWeight: detailTab === t.key ? 700 : 400,
            color: detailTab === t.key ? "var(--color-brand-600)" : "var(--color-surface-600)", marginBottom: "-1px",
          }}>{t.label}</button>
        ))}
      </div>

      {detailTab === "overview"  && <OverviewTab  source={source} isAdmin={isAdmin} />}
      {detailTab === "employees" && <EmployeesTab source={source} isAdmin={isAdmin} />}
      {detailTab === "logs"      && <LogsTab      source={source} />}
      {detailTab === "runs"      && <RunsTab      source={source} isAdmin={isAdmin} />}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ source, isAdmin }: { source: AttendanceSource; isAdmin: boolean }) {
  const [stats, setStats]       = useState<Stats | null>(null);
  const [recentRuns, setRuns]   = useState<IngestionRun[]>([]);
  const [sending, setSending]   = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [pickError, setPickError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const loadStats = () =>
    api.get<Stats>(`/admin/attendance-sources/${source.id}/stats`).then(r => setStats(r.data)).catch((err) => logError("AttendanceSourcesPage.OverviewTab.loadStats", err));

  useEffect(() => {
    void loadStats();
    void api.get<{ data: IngestionRun[] }>(`/admin/attendance-sources/${source.id}/runs?limit=5`).then(r => setRuns(r.data.data)).catch((err) => logError("AttendanceSourcesPage.OverviewTab.loadRuns", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.id]);

  // Resumes polling on mount (e.g. after a page reload) whenever a prior upload is still processing.
  useEffect(() => {
    if (stats?.uploadStatus !== "PROCESSING") return;
    const t = setInterval(() => { void loadStats(); }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats?.uploadStatus, source.id]);

  const handleUpload = async (file: File) => {
    setSending(true); setPickError(""); setSendProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/admin/attendance-sources/${source.id}/upload`, formData, {
        onUploadProgress: (evt) => {
          if (evt.total) setSendProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      await loadStats(); // picks up uploadStatus: "PROCESSING" — the effect above takes over polling from here
    } catch (err: unknown) {
      setPickError(`Failed to start upload: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setSending(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Stats */}
      {stats && (
        <div style={{ ...card }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, margin: "0 0 12px", color: "var(--color-surface-600)" }}>Data Summary</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}>
            {[
              { label: "Total Logs",        val: stats.totalLogs.toLocaleString() },
              { label: "Employees",         val: stats.totalEmployees.toString() },
              { label: "Mapped",            val: stats.mappedEmployees.toString() },
              { label: "Unmapped",          val: stats.unmappedEmployees.toString() },
              { label: "Unmapped Logs",     val: stats.unmappedLogs.toLocaleString() },
              { label: "Watermark",         val: stats.lastWatermark.toLocaleString() },
            ].map(s => (
              <div key={s.label} style={statBlock}>
                <div style={{ fontSize: "20px", fontWeight: 700 }}>{s.val}</div>
                <div style={{ fontSize: "11px", color: "var(--color-surface-500)", marginTop: "2px" }}>{s.label}</div>
              </div>
            ))}
          </div>
          {(stats.dateRange.min || stats.dateRange.max) && (
            <div style={{ fontSize: "12px", color: "var(--color-surface-500)", marginTop: "10px" }}>
              Date range: {fmtDate(stats.dateRange.min)} → {fmtDate(stats.dateRange.max)}
            </div>
          )}
        </div>
      )}

      {/* Upload */}
      {isAdmin && (
        <div style={{ ...card }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, margin: "0 0 8px", color: "var(--color-surface-600)" }}>Import MDB File</h3>
          <p style={{ fontSize: "12px", color: "var(--color-surface-500)", margin: "0 0 10px" }}>
            Upload a new eTimeTrackLite backup. Only logs newer than the current watermark will be inserted. Large files process in the background — you can leave this page and come back.
          </p>
          <input ref={fileRef} type="file" accept=".mdb" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) void handleUpload(f); e.target.value = ""; }} />
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button style={btnPrimary} disabled={sending || stats?.uploadStatus === "PROCESSING"} onClick={() => fileRef.current?.click()}>
              {sending ? `Uploading… ${sendProgress}%` : stats?.uploadStatus === "PROCESSING" ? "Processing…" : "Choose MDB File"}
            </button>
            {pickError && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "13px" }}>
                <XCircle size={14} /> {pickError}
              </span>
            )}
            {!pickError && stats?.uploadStatus === "DONE" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "13px" }}>
                <CheckCircle2 size={14} />
                Done — {(stats.uploadLogsAdded ?? 0).toLocaleString()} logs added, {stats.uploadEmployeesSynced ?? 0} employees synced, {(stats.uploadSkipped ?? 0).toLocaleString()} duplicates skipped.
              </span>
            )}
            {!pickError && stats?.uploadStatus === "FAILED" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "13px" }}>
                <XCircle size={14} /> Upload failed: {stats.uploadError ?? "Unknown error"}
              </span>
            )}
          </div>
          {sending && (
            <div style={{ marginTop: "10px" }}>
              <Progress value={sendProgress} />
              <div style={{ fontSize: "12px", color: "var(--color-surface-500)", marginTop: "6px" }}>
                Sending file to server… {sendProgress}%
              </div>
            </div>
          )}
          {!sending && stats?.uploadStatus === "PROCESSING" && (
            <div style={{ marginTop: "10px" }}>
              {(() => {
                const cur = stats.uploadProgressCurrent ?? 0;
                const total = stats.uploadProgressTotal ?? 0;
                const pct = total > 0 ? Math.min(100, Math.round((cur / total) * 100)) : 0;
                return (
                  <>
                    <Progress value={pct} />
                    <div style={{ fontSize: "12px", color: "var(--color-surface-500)", marginTop: "6px" }}>
                      {total > 0 ? `${pct}% — ` : ""}{stats.uploadProgressNote ?? "Processing…"}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Recent runs */}
      {recentRuns.length > 0 && (
        <div style={{ ...card }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, margin: "0 0 10px", color: "var(--color-surface-600)" }}>Recent Ingestion Runs</h3>
          <table style={tableStyle}>
            <thead><tr><th style={th}>Period</th><th style={th}>Status</th><th style={th}>Created</th><th style={th}>Updated</th><th style={th}>Unmatched</th><th style={th}>Started</th></tr></thead>
            <tbody>
              {recentRuns.map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.periodLabel ?? "—"}</td>
                  <td style={td}><StatusChip status={r.status} /></td>
                  <td style={td}>{r.attendanceCreated}</td>
                  <td style={td}>{r.attendanceUpdated}</td>
                  <td style={td}>{r.unmatchedCount}</td>
                  <td style={td}>{fmtDateTime(r.startedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Employees Tab ────────────────────────────────────────────────────────────

function EmployeesTab({ source, isAdmin }: { source: AttendanceSource; isAdmin: boolean }) {
  const [employees, setEmployees]     = useState<SourceEmployee[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [hrmsEmps, setHrmsEmps]       = useState<HRMSEmployee[]>([]);
  const [search, setSearch]           = useState("");
  const [autoMsg, setAutoMsg]         = useState("");
  const [loading, setLoading]         = useState(false);

  const load = async () => {
    const [empRes, sugRes] = await Promise.all([
      api.get<SourceEmployee[]>(`/admin/attendance-sources/${source.id}/employees`),
      api.get<Suggestion[]>(`/admin/attendance-sources/${source.id}/employees/suggestions`),
    ]);
    setEmployees(empRes.data);
    setSuggestions(sugRes.data);
  };

  useEffect(() => {
    void load();
    void api.get<{ data: HRMSEmployee[] }>("/employees?limit=500").then(r => setHrmsEmps(r.data.data ?? [])).catch((err) => logError("AttendanceSourcesPage.EmployeesTab.loadHRMSEmployees", err));
  }, [source.id]);

  const autoMap = async () => {
    setLoading(true); setAutoMsg("");
    try {
      const { data } = await api.post<{ mapped: number; total: number }>(`/admin/attendance-sources/${source.id}/employees/auto-map`, {});
      setAutoMsg(`Auto-mapped ${data.mapped} of ${data.total} unmapped employees`);
      void load();
    } catch (err) {
      logError("AttendanceSourcesPage.EmployeesTab.autoMap", err);
      setAutoMsg("Auto-map failed");
      throw err;
    }
    finally { setLoading(false); }
  };

  const setMapping = async (eid: string, employeeId: string | null) => {
    await api.patch(`/admin/attendance-sources/${source.id}/employees/${eid}`, { employeeId });
    void load();
  };

  const filtered = employees.filter(e =>
    !search || e.externalCode.toLowerCase().includes(search.toLowerCase()) || e.externalName.toLowerCase().includes(search.toLowerCase())
  );
  const unmapped = employees.filter(e => !e.employeeId).length;
  const suggestionSet = new Set(suggestions.map(s => s.sourceEmployeeId));

  return (
    <div style={{ ...card }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <input style={{ ...inputStyle, width: "220px" }} placeholder="Search employees…" value={search} onChange={e => setSearch(e.target.value)} />
          <span style={{ fontSize: "12px", color: "var(--color-surface-500)" }}>{employees.length} total · {unmapped} unmapped</span>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {autoMsg && <span style={{ fontSize: "12px", color: "var(--color-surface-500)" }}>{autoMsg}</span>}
            <AsyncButton variant="outline" animated={false} onClick={() => autoMap()} disabled={loading}>Auto-Map by Code</AsyncButton>
          </div>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Ext. Code</th>
              <th style={th}>Name in Device</th>
              <th style={th}>HRMS Employee</th>
              <th style={th}>Status</th>
              {isAdmin && <th style={th}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => {
              const hasSuggestion = suggestionSet.has(e.id);
              const suggestion = suggestions.find(s => s.sourceEmployeeId === e.id);
              return (
                <tr key={e.id}>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>{e.externalCode}</td>
                  <td style={td}>{e.externalName}</td>
                  <td style={td}>
                    {e.employee
                      ? `${e.employee.firstName} ${e.employee.lastName} (${e.employee.employeeCode})`
                      : suggestion
                        ? <span style={{ color: "var(--color-brand-600)", fontSize: "12px" }}>Suggestion: {suggestion.suggestedEmployee.firstName} {suggestion.suggestedEmployee.lastName}</span>
                        : "—"}
                  </td>
                  <td style={td}>
                    {e.employeeId
                      ? <span style={chip("var(--color-success-fg)", "var(--color-success-bg)")}>Mapped</span>
                      : hasSuggestion
                        ? <span style={chip("var(--color-warning-fg)", "var(--color-warning-bg)")}>Suggested</span>
                        : <span style={chip("var(--color-error-fg)", "var(--color-error-bg)")}>Unmapped</span>}
                  </td>
                  {isAdmin && (
                    <td style={td}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {!e.employeeId && suggestion && (
                          <button style={{ ...btnSecondary, fontSize: "11px", padding: "3px 8px" }}
                            onClick={() => void setMapping(e.id, suggestion.suggestedEmployee.id)}>
                            Accept
                          </button>
                        )}
                        {e.employeeId && (
                          <button style={{ ...btnSecondary, fontSize: "11px", padding: "3px 8px" }}
                            onClick={() => void setMapping(e.id, null)}>
                            Unmap
                          </button>
                        )}
                        <EmpSelect hrmsEmps={hrmsEmps} onSelect={hid => void setMapping(e.id, hid)} />
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmpSelect({ hrmsEmps, onSelect }: { hrmsEmps: HRMSEmployee[]; onSelect: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const filtered = hrmsEmps.filter(e =>
    `${e.firstName} ${e.lastName} ${e.employeeCode}`.toLowerCase().includes(q.toLowerCase())
  ).slice(0, 15);

  if (!open) return <button style={{ ...btnSecondary, fontSize: "11px", padding: "3px 8px" }} onClick={() => setOpen(true)}>Assign</button>;
  return (
    <div style={{ position: "relative" }}>
      <input style={{ ...inputStyle, width: "160px", fontSize: "12px" }} autoFocus placeholder="Search HRMS…" value={q} onChange={e => setQ(e.target.value)} onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {filtered.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", minWidth: "200px" }}>
          {filtered.map(e => (
            <div key={e.id} style={{ padding: "7px 10px", cursor: "pointer", fontSize: "12px" }}
              onMouseDown={() => { onSelect(e.id); setOpen(false); setQ(""); }}>
              {e.firstName} {e.lastName} <span style={{ color: "var(--color-surface-400)" }}>({e.employeeCode})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Logs Tab (Insights Dashboard) ──────────────────────────────────────────

function LogsTab({ source }: { source: AttendanceSource }) {
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [subTab, setSubTab]           = useState<LogSubTab>("daily");
  const [from, setFrom]               = useState(thirtyAgo);
  const [to, setTo]                   = useState(today);
  const [empFilter, setEmpFilter]     = useState("");
  const [empSearch, setEmpSearch]     = useState("");
  const [searchOpen, setSearchOpen]   = useState(false);
  const [dailyRows, setDailyRows]     = useState<DailyRow[]>([]);
  const [patternRows, setPatternRows] = useState<EmpPatternRow[]>([]);
  const [lateRows, setLateRows]       = useState<LateRow[]>([]);
  const [lateHour, setLateHour]       = useState(10);
  const [lateMinute, setLateMinute]   = useState(0);
  const [pendingThreshold, setPendingThreshold] = useState("10:00");
  const [bannerKey, setBannerKey]     = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function outside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (from) p.set("from", new Date(from).toISOString());
    if (to)   p.set("to",   new Date(to + "T23:59:59").toISOString());
    const lp = new URLSearchParams(p);
    lp.set("lateHour", String(lateHour));
    lp.set("lateMinute", String(lateMinute));
    void Promise.allSettled([
      api.get<DailyRow[]>(`/admin/attendance-sources/${source.id}/reports/daily?${p}`),
      api.get<EmpPatternRow[]>(`/admin/attendance-sources/${source.id}/reports/employees?${p}`),
      api.get<LateRow[]>(`/admin/attendance-sources/${source.id}/reports/late?${lp}`),
    ]).then(([dr, pr, lr]) => {
      if (dr.status === "fulfilled") setDailyRows(dr.value.data);
      if (pr.status === "fulfilled") setPatternRows(pr.value.data);
      if (lr.status === "fulfilled") setLateRows(lr.value.data);
      setBannerKey(k => k + 1);
    });
  }, [source.id, from, to, lateHour, lateMinute]);

  function applyThreshold() {
    const parts = pendingThreshold.split(":");
    const h = parseInt(parts[0] ?? "10", 10);
    const m = parseInt(parts[1] ?? "0", 10);
    if (!isNaN(h) && !isNaN(m)) { setLateHour(h); setLateMinute(m); }
  }

  const searchResults = empSearch.length >= 1
    ? patternRows.filter(r =>
        r.externalCode.toLowerCase().includes(empSearch.toLowerCase()) ||
        r.displayName.toLowerCase().includes(empSearch.toLowerCase())
      ).slice(0, 10)
    : [];

  const empLabel = empFilter
    ? (patternRows.find(r => r.externalCode === empFilter)?.displayName ?? empFilter)
    : "";

  const workingDays = dailyRows.filter(r => new Date(r.date + "T00:00:00").getDay() !== 0).length;

  const subTabs: { key: LogSubTab; label: string }[] = [
    { key: "daily",    label: "Daily Summary"     },
    { key: "patterns", label: "Employee Patterns" },
    { key: "late",     label: "Late Arrivals"     },
    { key: "table",    label: "Log Table"         },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <DatePicker style={{ ...inputStyle, width: "140px" }} value={from} onChange={setFrom} />
          <span style={{ color: "var(--color-surface-400)" }}>→</span>
          <DatePicker style={{ ...inputStyle, width: "140px" }} value={to} onChange={setTo} />
        </div>
        <div ref={searchRef} style={{ position: "relative" }}>
          {empFilter ? (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px",
              background: "var(--color-info-bg)", border: "1px solid var(--color-info-border)", borderRadius: "var(--radius-md)", fontSize: "13px" }}>
              <span style={{ color: "var(--color-info-fg)", fontWeight: 500 }}>{empLabel} ({empFilter})</span>
              <button onClick={() => { setEmpFilter(""); setEmpSearch(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-info)", fontWeight: 700, lineHeight: "1", padding: "0 2px" }}>×</button>
            </div>
          ) : (
            <div style={{ position: "relative", width: "240px" }}>
              <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--color-surface-400)", pointerEvents: "none" }} />
              <input style={{ ...inputStyle, width: "240px", paddingLeft: "30px" }} placeholder="Search employee (name or code)…"
                value={empSearch}
                onChange={e => { setEmpSearch(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)} />
            </div>
          )}
          {searchOpen && searchResults.length > 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 200,
              background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)",
              borderRadius: "var(--radius-md)", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", minWidth: "260px" }}>
              {searchResults.map(r => (
                <div key={r.externalCode} style={{ padding: "8px 12px", cursor: "pointer", fontSize: "13px" }}
                  onMouseDown={() => { setEmpFilter(r.externalCode); setEmpSearch(""); setSearchOpen(false); }}>
                  {r.displayName}
                  <span style={{ color: "var(--color-surface-400)", fontSize: "11px", marginLeft: "6px" }}>· code {r.externalCode}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <DataQualityBanner patternRows={patternRows} dailyRows={dailyRows} bannerKey={bannerKey} />

      <div style={{ display: "flex", gap: "4px", marginBottom: "16px" }}>
        {subTabs.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            padding: "6px 12px", border: "1px solid var(--color-surface-200)",
            borderRadius: "var(--radius-md)", fontSize: "12px", fontWeight: subTab === t.key ? 700 : 400,
            background: subTab === t.key ? "var(--color-brand-600)" : "var(--color-surface-0)",
            color: subTab === t.key ? "#fff" : "var(--color-surface-700)", cursor: "pointer",
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === "daily"    && <DailySummary rows={dailyRows} />}
      {subTab === "patterns" && <EmpPatterns rows={patternRows} lateRows={lateRows} empFilter={empFilter} onEmpSelect={setEmpFilter} />}
      {subTab === "late"     && (
        <LateArrivals rows={lateRows} patternRows={patternRows} from={from} to={to}
          lateThreshold={{ hour: lateHour, minute: lateMinute }}
          pendingThreshold={pendingThreshold} onPendingChange={setPendingThreshold}
          onApplyThreshold={applyThreshold} workingDays={workingDays} sourceId={source.id} />
      )}
      {subTab === "table"    && <LogTable source={source} from={from} to={to} empFilter={empFilter} />}
    </div>
  );
}

// ─── Data Quality Banner ──────────────────────────────────────────────────────

function DataQualityBanner({ patternRows, dailyRows, bannerKey }: {
  patternRows: EmpPatternRow[]; dailyRows: DailyRow[]; bannerKey: number;
}) {
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { setDismissed(false); }, [bannerKey]);

  const unmapped = patternRows.filter(r => r.displayName === r.externalCode);
  const singlePunchEmps = patternRows.filter(r => r.singlePunchDays > 0).length;
  const maxPresent = Math.max(...dailyRows.map(r => r.present), 1);
  const nearZeroDays = dailyRows.filter(r =>
    r.present < 0.3 * maxPresent && new Date(r.date + "T00:00:00").getDay() !== 6
  ).length;

  const issues: string[] = [
    unmapped.length > 0
      ? `${unmapped.length} employee code(s) may be unmapped (${unmapped.slice(0, 4).map(r => r.externalCode).join(", ")}${unmapped.length > 4 ? "…" : ""}) — exclude from name-based filters until resolved`
      : "",
    singlePunchEmps > 0
      ? `${singlePunchEmps} employee(s) have ≥1 single-punch day — missed clock-in/out; review device sync logs`
      : "",
    nearZeroDays > 0
      ? `${nearZeroDays} non-Saturday day(s) with near-zero attendance — possible holidays or device sync failures`
      : "",
  ].filter(Boolean);

  if (issues.length === 0 || dismissed) return null;

  return (
    <div style={{ marginBottom: "12px", padding: "10px 14px", background: "var(--color-warning-bg-subtle)",
      border: "1px solid var(--color-warning-border)", borderRadius: "var(--radius-md)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "12px", fontWeight: 700, color: "var(--color-warning-fg)", marginBottom: "4px" }}>
            <AlertTriangle size={13} /> Data Quality: {issues.length} issue{issues.length > 1 ? "s" : ""} detected in this range
          </div>
          {issues.map((iss, i) => (
            <div key={i} style={{ fontSize: "12px", color: "var(--color-warning-fg-strong)", marginTop: "2px" }}>• {iss}</div>
          ))}
        </div>
        <button onClick={() => setDismissed(true)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-warning-fg)", fontSize: "13px", whiteSpace: "nowrap" }}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ─── Log Table ────────────────────────────────────────────────────────────────

function LogTable({ source, from, to, empFilter }: {
  source: AttendanceSource; from: string; to: string; empFilter: string;
}) {
  const [logs, setLogs]   = useState<RawLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage]   = useState(1);
  const [limit, setLimit] = useState(25);

  useEffect(() => { setPage(1); }, [from, to, empFilter, source.id]);

  useEffect(() => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (from)      params.set("from",            new Date(from).toISOString());
    if (to)        params.set("to",              new Date(to + "T23:59:59").toISOString());
    if (empFilter) params.set("externalEmpCode", empFilter);
    void api.get<{ total: number; data: RawLog[] }>(`/admin/attendance-sources/${source.id}/logs?${params}`)
      .then(r => { setLogs(r.data.data); setTotal(r.data.total); })
      .catch((err) => logError("AttendanceSourcesPage.LogTable.loadLogs", err));
  }, [source.id, page, limit, from, to, empFilter]);

  return (
    <div style={{ ...card }}>
      <div style={{ marginBottom: "10px", fontSize: "12px", color: "var(--color-surface-500)" }}>
        {total.toLocaleString()} records{empFilter ? ` · filtered to code ${empFilter}` : ""}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead><tr>
            <th style={th}>Log ID</th><th style={th}>Date/Time (IST)</th>
            <th style={th}>Emp Code</th><th style={th}>Direction</th><th style={th}>Device</th>
          </tr></thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id}>
                <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>{l.externalLogId}</td>
                <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>{new Date(l.logDateTime).toLocaleString("en-IN")}</td>
                <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>{l.externalEmpCode}</td>
                <td style={td}>{l.direction ?? "—"}</td>
                <td style={td}>{l.deviceId ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        meta={{ total, page, limit, totalPages: Math.max(1, Math.ceil(total / limit)) }}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
      />
    </div>
  );
}

// ─── Daily Summary ────────────────────────────────────────────────────────────

function DailySummary({ rows }: { rows: DailyRow[] }) {
  const [view, setView] = useState<"chart" | "heatmap" | "table">("chart");
  const [tooltip, setTooltip] = useState<{ x: number; y: number; row: DailyRow } | null>(null);

  const W = 800, H = 200;
  const PAD = { t: 20, r: 20, b: 50, l: 45 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;
  const maxVal = Math.max(...rows.map(r => r.present), 1);
  const avgPresent = rows.length > 0 ? rows.reduce((s, r) => s + r.present, 0) / rows.length : 0;
  const xScale = (i: number) =>
    rows.length <= 1 ? PAD.l + cW / 2 : PAD.l + (i / (rows.length - 1)) * cW;
  const yScale = (v: number) => PAD.t + cH - (v / maxVal) * cH;

  const presentArea = rows.length > 0
    ? `M ${xScale(0)} ${PAD.t + cH} ` +
      rows.map((r, i) => `L ${xScale(i)} ${yScale(r.present)}`).join(" ") +
      ` L ${xScale(rows.length - 1)} ${PAD.t + cH} Z`
    : "";
  const absentArea = rows.length > 0
    ? `M ${xScale(0)} ${PAD.t + cH} ` +
      rows.map((r, i) => `L ${xScale(i)} ${yScale(r.absent)}`).join(" ") +
      ` L ${xScale(rows.length - 1)} ${PAD.t + cH} Z`
    : "";

  const yTicks = [0, Math.round(maxVal / 2), maxVal];
  const N = Math.max(1, Math.ceil(rows.length / 12));

  const tooltipIdx = tooltip ? rows.findIndex(r => r.date === tooltip.row.date) : -1;

  function handleSvgMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) * (W / rect.width);
    const idx = Math.max(0, Math.min(rows.length - 1,
      Math.round(((svgX - PAD.l) / cW) * (rows.length - 1))
    ));
    const row = rows[idx];
    if (row) setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, row });
  }

  // Heatmap: assign calendar week index and day-of-week to each row
  const heatCells = (() => {
    if (rows.length === 0) return [] as { date: string; present: number; absent: number; singlePunch: number; dow: number; wk: number }[];
    const first = rows[0]!;
    const firstDate = new Date(first.date + "T00:00:00");
    const startDow = firstDate.getDay();
    return rows.map(r => {
      const d = new Date(r.date + "T00:00:00");
      const offset = Math.round((d.getTime() - firstDate.getTime()) / 86400000) + startDow;
      return { ...r, dow: d.getDay(), wk: Math.floor(offset / 7) };
    });
  })();
  const numWeeks = heatCells.length > 0 ? Math.max(...heatCells.map(c => c.wk)) + 1 : 1;

  function cellColor(present: number, dow: number): string {
    if (dow === 0) return "var(--color-surface-100)";
    const ratio = present / maxVal;
    if (ratio < 0.05) return "var(--color-error-border)";
    if (ratio >= 0.6) return "var(--color-success-accent)";
    if (ratio >= 0.3) return "var(--color-success-border)";
    return "var(--color-warning-border)";
  }

  return (
    <div style={{ ...card }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-surface-700)" }}>Daily Attendance</span>
        <div style={{ display: "flex", gap: "4px" }}>
          {(["chart", "heatmap", "table"] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "4px 10px", border: "1px solid var(--color-surface-200)",
              borderRadius: "var(--radius-md)", fontSize: "11px", fontWeight: view === v ? 700 : 400,
              background: view === v ? "var(--color-brand-600)" : "var(--color-surface-0)",
              color: view === v ? "#fff" : "var(--color-surface-600)", cursor: "pointer", textTransform: "capitalize",
            }}>{v}</button>
          ))}
        </div>
      </div>

      {rows.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px", color: "var(--color-surface-400)", fontSize: "13px" }}>
          No data for this range
        </div>
      )}

      {view === "chart" && rows.length > 0 && (
        <div style={{ position: "relative" }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", cursor: "crosshair" }}
            onMouseMove={handleSvgMove} onMouseLeave={() => setTooltip(null)}>
            {yTicks.map(v => (
              <g key={v}>
                <line x1={PAD.l} y1={yScale(v)} x2={PAD.l + cW} y2={yScale(v)} stroke="var(--color-border)" strokeWidth="1" />
                <text x={PAD.l - 6} y={yScale(v) + 4} textAnchor="end" fontSize="10" fill="var(--color-text-400)">{v}</text>
              </g>
            ))}
            <path d={absentArea} fill="var(--color-error-border)" fillOpacity="0.5" />
            <polyline points={rows.map((r, i) => `${xScale(i)},${yScale(r.absent)}`).join(" ")}
              fill="none" stroke="var(--color-error)" strokeWidth="1.5" />
            <path d={presentArea} fill="var(--color-success-border)" fillOpacity="0.5" />
            <polyline points={rows.map((r, i) => `${xScale(i)},${yScale(r.present)}`).join(" ")}
              fill="none" stroke="var(--color-success)" strokeWidth="2" />
            {rows.map((r, i) => {
              const dow = new Date(r.date + "T00:00:00").getDay();
              const x = xScale(i);
              return (
                <g key={r.date}>
                  {r.present < 0.3 * maxVal && dow !== 6 && dow !== 0 && (
                    <polygon points={`${x},${yScale(r.present) - 7} ${x + 5},${yScale(r.present)} ${x},${yScale(r.present) + 7} ${x - 5},${yScale(r.present)}`}
                      fill="var(--color-error)" />
                  )}
                  {dow === 6 && r.present < 0.7 * avgPresent && (
                    <text x={x} y={PAD.t + cH + 30} textAnchor="middle" fontSize="9" fill="var(--color-text-400)">Sat</text>
                  )}
                  {r.singlePunch > 15 && (
                    <circle cx={x} cy={yScale(r.singlePunch)} r="4" fill="var(--color-warning)" />
                  )}
                  {i % N === 0 && (
                    <text x={x} y={PAD.t + cH + 14} textAnchor="middle" fontSize="9" fill="var(--color-text-400)">{r.date.slice(5)}</text>
                  )}
                </g>
              );
            })}
            {tooltip && tooltipIdx >= 0 && (
              <line x1={xScale(tooltipIdx)} y1={PAD.t} x2={xScale(tooltipIdx)} y2={PAD.t + cH}
                stroke="var(--color-text-secondary)" strokeWidth="1" strokeDasharray="4 2" />
            )}
          </svg>
          <div style={{ display: "flex", gap: "16px", fontSize: "11px", color: "var(--color-surface-500)", marginTop: "6px" }}>
            <span><span style={{ display: "inline-block", width: "12px", height: "3px", background: "var(--color-success)", verticalAlign: "middle", marginRight: "4px" }} />Present</span>
            <span><span style={{ display: "inline-block", width: "12px", height: "3px", background: "var(--color-error)", verticalAlign: "middle", marginRight: "4px" }} />Absent</span>
            <span><span style={{ display: "inline-block", width: "8px", height: "8px", background: "var(--color-error)", transform: "rotate(45deg)", verticalAlign: "middle", marginRight: "4px" }} />Anomaly</span>
            <span><span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "9999px", background: "var(--color-warning)", verticalAlign: "middle", marginRight: "4px" }} />Single-punch spike</span>
          </div>
          {tooltip && (
            <div style={{ position: "absolute", top: Math.max(0, tooltip.y - 70), left: Math.min(tooltip.x + 10, 550),
              background: "rgba(17,24,39,0.9)", color: "#fff", borderRadius: "var(--radius-md)",
              padding: "8px 12px", fontSize: "12px", pointerEvents: "none", zIndex: 100, whiteSpace: "nowrap" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>{tooltip.row.date}</div>
              <div>Present: <b>{tooltip.row.present}</b></div>
              <div>Absent: <b>{tooltip.row.absent}</b></div>
              <div>Single Punch: <b>{tooltip.row.singlePunch}</b></div>
            </div>
          )}
        </div>
      )}

      {view === "heatmap" && rows.length > 0 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "3px", marginBottom: "6px" }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
              <div key={d} style={{ fontSize: "10px", textAlign: "center", color: "var(--color-surface-400)", fontWeight: 600 }}>{d}</div>
            ))}
          </div>
          {Array.from({ length: numWeeks }, (_, wk) => (
            <div key={wk} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "3px", marginBottom: "3px" }}>
              {Array.from({ length: 7 }, (_, dow) => {
                const cell = heatCells.find(c => c.wk === wk && c.dow === dow);
                return cell
                  ? <div key={dow} title={`${cell.date}: ${cell.present} present, ${cell.absent} absent, ${cell.singlePunch} single-punch`}
                      style={{ aspectRatio: "1", borderRadius: "3px", background: cellColor(cell.present, dow) }} />
                  : <div key={dow} style={{ aspectRatio: "1", borderRadius: "3px", background: "var(--color-surface-50)" }} />;
              })}
            </div>
          ))}
          <div style={{ display: "flex", gap: "12px", fontSize: "11px", color: "var(--color-surface-500)", marginTop: "8px" }}>
            <span><span style={{ display: "inline-block", width: "12px", height: "12px", background: "var(--color-success-accent)", borderRadius: "2px", verticalAlign: "middle", marginRight: "4px" }} />High</span>
            <span><span style={{ display: "inline-block", width: "12px", height: "12px", background: "var(--color-success-border)", borderRadius: "2px", verticalAlign: "middle", marginRight: "4px" }} />Partial</span>
            <span><span style={{ display: "inline-block", width: "12px", height: "12px", background: "var(--color-error-border)", borderRadius: "2px", verticalAlign: "middle", marginRight: "4px" }} />Near-zero</span>
            <span><span style={{ display: "inline-block", width: "12px", height: "12px", background: "var(--color-surface-100)", borderRadius: "2px", verticalAlign: "middle", marginRight: "4px" }} />Sunday / no data</span>
          </div>
        </div>
      )}

      {view === "table" && rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Present</th><th style={th}>Absent</th><th style={th}>Single Punch</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.date}>
                  <td style={{ ...td, fontWeight: 500 }}>{r.date}</td>
                  <td style={td}><span style={chip("var(--color-success-fg)", "var(--color-success-bg)")}>{r.present}</span></td>
                  <td style={td}><span style={chip("var(--color-error-fg)", "var(--color-error-bg)")}>{r.absent}</span></td>
                  <td style={td}><span style={chip("var(--color-warning-fg)", "var(--color-warning-bg)")}>{r.singlePunch}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Employee Patterns ────────────────────────────────────────────────────────

function EmpPatterns({ rows, lateRows, empFilter, onEmpSelect }: {
  rows: EmpPatternRow[]; lateRows: LateRow[]; empFilter: string; onEmpSelect: (code: string) => void;
}) {
  const [expandedCode, setExpandedCode] = useState<string | null>(null);

  const filtered = rows.filter(r =>
    !empFilter ||
    r.externalCode.toLowerCase().includes(empFilter.toLowerCase()) ||
    r.displayName.toLowerCase().includes(empFilter.toLowerCase())
  );

  function getFlags(r: EmpPatternRow): { label: string; icon: typeof AlertCircle; color: string; bg: string }[] {
    const f: { label: string; icon: typeof AlertCircle; color: string; bg: string }[] = [];
    if (r.displayName === r.externalCode) f.push({ label: "Unmapped", icon: HelpCircle, color: "var(--color-accent-fg-strong)", bg: "var(--color-accent-bg)" });
    if (r.singlePunchDays >= 5)      f.push({ label: "High Risk", icon: AlertCircle, color: "var(--color-error-fg)", bg: "var(--color-error-bg)" });
    else if (r.singlePunchDays >= 2) f.push({ label: "Medium",    icon: AlertCircle, color: "var(--color-warning-fg)", bg: "var(--color-warning-bg)" });
    return f;
  }

  return (
    <div style={{ ...card }}>
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Code</th><th style={th}>Employee</th><th style={th}>Days</th>
              <th style={th}>Avg In</th><th style={th}>Avg Out</th><th style={th}>Single Punch</th><th style={th}>Flags</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const isExpanded = expandedCode === r.externalCode;
              const flags = getFlags(r);
              const late = lateRows.find(l => l.externalCode === r.externalCode);
              const lateRate = late && r.daysWorked > 0 ? Math.round((late.lateDays / r.daysWorked) * 100) : null;
              const badPair = r.avgInTime != null && r.avgOutTime != null &&
                r.avgInTime > r.avgOutTime && r.avgInTime > "18:00";
              return (
                <Fragment key={r.externalCode}>
                  <tr onClick={() => setExpandedCode(isExpanded ? null : r.externalCode)}
                    style={{ cursor: "pointer", background: isExpanded ? "var(--color-surface-50)" : undefined }}>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: "12px" }}>{r.externalCode}</td>
                    <td style={{ ...td, fontWeight: 500 }}>{r.displayName}</td>
                    <td style={td}>{r.daysWorked}</td>
                    <td style={{ ...td, fontFamily: "monospace" }}>{r.avgInTime ?? "—"}</td>
                    <td style={{ ...td, fontFamily: "monospace" }}>{r.avgOutTime ?? "—"}</td>
                    <td style={td}>
                      {r.singlePunchDays > 0
                        ? <span style={chip("var(--color-warning-fg)", "var(--color-warning-bg)")}>{r.singlePunchDays}</span>
                        : "0"}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                        {flags.map(f => <span key={f.label} style={{ ...chip(f.color, f.bg), fontSize: "10px", display: "inline-flex", alignItems: "center", gap: 3 }}><f.icon size={10} />{f.label}</span>)}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: "var(--color-surface-50)" }}>
                      <td colSpan={7} style={{ padding: "10px 16px", borderBottom: "1px solid var(--color-surface-200)" }}>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "12px", alignItems: "center" }}>
                          {badPair && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--color-warning-fg)", background: "var(--color-warning-bg)", padding: "3px 8px", borderRadius: "var(--radius-sm)" }}>
                              <AlertTriangle size={12} /> Avg out earlier than avg in — possible overnight shift or bad punch pairing
                            </span>
                          )}
                          {late ? (
                            <span>
                              Late days: <b>{late.lateDays}</b>
                              {lateRate !== null && <span style={{ color: "var(--color-surface-400)", marginLeft: "4px" }}>({lateRate}% rate)</span>}
                              {late.avgLateTime && <span style={{ fontFamily: "monospace", marginLeft: "8px" }}>avg {late.avgLateTime}</span>}
                            </span>
                          ) : (
                            <span style={{ color: "var(--color-surface-400)" }}>No late arrivals in this range</span>
                          )}
                          <button onClick={e => { e.stopPropagation(); onEmpSelect(r.externalCode); }}
                            style={{ ...btnSecondary, fontSize: "11px", padding: "3px 8px" }}>
                            Filter all tabs →
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--color-surface-400)" }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Late Arrivals ────────────────────────────────────────────────────────────

function LateArrivals({ rows, patternRows, from, to, lateThreshold, pendingThreshold, onPendingChange, onApplyThreshold, workingDays, sourceId }: {
  rows: LateRow[]; patternRows: EmpPatternRow[];
  from: string; to: string;
  lateThreshold: { hour: number; minute: number };
  pendingThreshold: string; onPendingChange: (v: string) => void;
  onApplyThreshold: () => void;
  workingDays: number; sourceId: string;
}) {
  const [drillCode, setDrillCode]     = useState<string | null>(null);
  const [drillData, setDrillData]     = useState<{ date: string; time: string; late: boolean }[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const maxLateDays = Math.max(...rows.map(r => r.lateDays), 1);
  const priorityThreshold = Math.max(1, Math.round(workingDays * 0.9));
  const priority = rows.filter(r => r.lateDays >= priorityThreshold);
  const others   = rows.filter(r => r.lateDays < priorityThreshold);

  async function handleDrill(code: string) {
    if (drillCode === code) { setDrillCode(null); setDrillData([]); return; }
    setDrillCode(code); setDrillLoading(true); setDrillData([]);
    try {
      const params = new URLSearchParams({ externalEmpCode: code, limit: "200" });
      if (from) params.set("from", new Date(from).toISOString());
      if (to)   params.set("to",   new Date(to + "T23:59:59").toISOString());
      const { data } = await api.get<{ data: RawLog[] }>(`/admin/attendance-sources/${sourceId}/logs?${params}`);
      const byDate = new Map<string, Date>();
      for (const log of data.data) {
        const dt = new Date(log.logDateTime);
        const d = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
        if (!byDate.has(d) || dt < byDate.get(d)!) byDate.set(d, dt);
      }
      const threshMins = lateThreshold.hour * 60 + lateThreshold.minute;
      setDrillData(
        Array.from(byDate.entries())
          .map(([date, dt]) => ({
            date,
            time: `${String(dt.getUTCHours()).padStart(2, "0")}:${String(dt.getUTCMinutes()).padStart(2, "0")}`,
            late: dt.getUTCHours() * 60 + dt.getUTCMinutes() > threshMins,
          }))
          .sort((a, b) => a.date.localeCompare(b.date))
      );
    } catch (err) { logError("AttendanceSourcesPage.LateArrivals.handleDrill", err); }
    finally { setDrillLoading(false); }
  }

  function BarEntry({ row, isPriority }: { row: LateRow; isPriority: boolean }) {
    const pattern = patternRows.find(p => p.externalCode === row.externalCode);
    const nightShift = isPriority && pattern?.avgInTime != null && pattern.avgInTime < "08:00";
    const isDrilled = drillCode === row.externalCode;
    return (
      <div key={row.externalCode}>
        <div onClick={() => void handleDrill(row.externalCode)}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "5px 0", cursor: "pointer",
            borderRadius: "var(--radius-sm)", background: isDrilled ? "var(--color-surface-50)" : undefined }}>
          <div style={{ width: "200px", minWidth: "140px", fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span style={{ fontWeight: 500 }}>{row.displayName}</span>
            <span style={{ color: "var(--color-surface-400)", fontSize: "11px", marginLeft: "4px" }}>({row.externalCode})</span>
          </div>
          <div style={{ flex: 1, height: "8px", background: "var(--color-surface-100)", borderRadius: "9999px", overflow: "hidden" }}>
            <div style={{ height: "8px", borderRadius: "9999px", transition: "width 0.4s ease",
              width: `${(row.lateDays / maxLateDays) * 100}%`,
              background: isPriority ? "var(--color-error)" : "var(--color-error-accent)" }} />
          </div>
          <div style={{ width: "110px", textAlign: "right", fontSize: "12px", color: "var(--color-surface-600)", whiteSpace: "nowrap" }}>
            {row.lateDays} days · {row.avgLateTime ?? "—"}
          </div>
        </div>
        {nightShift && (
          <div style={{ fontSize: "11px", color: "var(--color-text-secondary)", fontStyle: "italic", paddingLeft: "210px", paddingBottom: "4px" }}>
            Avg in {pattern!.avgInTime} — possible night shift; verify threshold
          </div>
        )}
        {isDrilled && (
          <div style={{ margin: "4px 0 8px 210px", padding: "8px 12px", background: "var(--color-surface-50)",
            border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: "12px" }}>
            {drillLoading ? (
              <span style={{ color: "var(--color-surface-400)" }}>Loading…</span>
            ) : drillData.length === 0 ? (
              <span style={{ color: "var(--color-surface-400)" }}>No raw logs found</span>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "4px" }}>
                {drillData.map(d => (
                  <span key={d.date} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "monospace", color: d.late ? "var(--color-error)" : "var(--color-success)" }}>
                    {d.date.slice(5)} {d.time}{d.late ? <Flag size={11} /> : <CheckCircle2 size={11} />}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...card }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-surface-700)" }}>Late Arrivals</span>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", marginLeft: "auto" }}>
          <span style={{ color: "var(--color-surface-500)" }}>Threshold:</span>
          <TimeInput value={pendingThreshold} onChange={onPendingChange} aria-label="Late arrival threshold" className="w-28" />
          <button style={{ ...btnSecondary, fontSize: "11px", padding: "4px 10px" }} onClick={onApplyThreshold}>Apply</button>
          <span style={{ color: "var(--color-surface-400)", fontSize: "11px" }}>IST</span>
        </div>
      </div>

      {rows.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px", color: "var(--color-surface-400)", fontSize: "13px" }}>
          No late arrivals in this range
        </div>
      )}

      {priority.length > 0 && (
        <div style={{ padding: "10px 12px", background: "var(--color-error-bg-subtle)", border: "1px solid var(--color-error-border)",
          borderRadius: "var(--radius-md)", marginBottom: "16px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "12px", fontWeight: 700, color: "var(--color-error-fg)", marginBottom: "8px" }}>
            <AlertCircle size={13} /> Priority Follow-Up — ≥{priorityThreshold} late days in range
          </div>
          {priority.map(row => <BarEntry key={row.externalCode} row={row} isPriority />)}
        </div>
      )}

      {others.length > 0 && (
        <div>
          {priority.length > 0 && (
            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "8px" }}>
              Other Late Employees
            </div>
          )}
          {others.map(row => <BarEntry key={row.externalCode} row={row} isPriority={false} />)}
        </div>
      )}
    </div>
  );
}

// ─── Runs Tab ─────────────────────────────────────────────────────────────────

function RunsTab({ source, isAdmin }: { source: AttendanceSource; isAdmin: boolean }) {
  const [runs, setRuns]     = useState<IngestionRun[]>([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [limit, setLimit]   = useState(25);
  const [fromDate, setFrom] = useState("");
  const [toDate, setTo]     = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]       = useState("");

  const load = async () => {
    const { data } = await api.get<{ total: number; data: IngestionRun[] }>(`/admin/attendance-sources/${source.id}/runs?page=${page}&limit=${limit}`);
    setRuns(data.data); setTotal(data.total);
  };

  useEffect(() => { void load(); }, [source.id, page, limit]);

  const ingest = async () => {
    if (!fromDate || !toDate) { setMsg("Select from and to dates"); throw new Error("validation"); }
    setLoading(true); setMsg("Running ingestion…");
    try {
      const { data } = await api.post<{ attendanceCreated: number; attendanceUpdated: number; unmatchedCount: number; flaggedForReview: number }>(
        `/admin/attendance-sources/${source.id}/ingest`, { fromDate, toDate }
      );
      setMsg(`Done — created: ${data.attendanceCreated}, updated: ${data.attendanceUpdated}, unmatched: ${data.unmatchedCount}, flagged for review: ${data.flaggedForReview}`);
      void load();
    } catch (err) {
      logError("AttendanceSourcesPage.RunsTab.ingest", err);
      setMsg("Ingestion failed");
      throw err;
    }
    finally { setLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {isAdmin && (
        <div style={{ ...card }}>
          <h3 style={{ fontSize: "13px", fontWeight: 600, margin: "0 0 10px", color: "var(--color-surface-600)" }}>Run Ingestion</h3>
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 500 }}>From</label>
              <DatePicker style={{ ...inputStyle, marginTop: "4px", width: "150px" }} value={fromDate} onChange={setFrom} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 500 }}>To</label>
              <DatePicker style={{ ...inputStyle, marginTop: "4px", width: "150px" }} value={toDate} onChange={setTo} />
            </div>
            <AsyncButton disabled={loading} onClick={() => ingest()}>
              Start Ingestion
            </AsyncButton>
            {msg && <span style={{ fontSize: "13px", color: "var(--color-surface-600)", alignSelf: "center" }}>{msg}</span>}
          </div>
        </div>
      )}

      <div style={{ ...card }}>
        <h3 style={{ fontSize: "13px", fontWeight: 600, margin: "0 0 10px", color: "var(--color-surface-600)" }}>Ingestion History</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Period</th>
                <th style={th}>Status</th>
                <th style={th}>Raw Logs</th>
                <th style={th}>Created</th>
                <th style={th}>Updated</th>
                <th style={th}>Unmatched</th>
                <th style={th}>Flagged</th>
                <th style={th}>Duration</th>
                <th style={th}>Started</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => {
                const durationMs = r.completedAt ? new Date(r.completedAt).getTime() - new Date(r.startedAt).getTime() : null;
                const duration   = durationMs !== null ? `${Math.round(durationMs / 1000)}s` : "—";
                return (
                  <tr key={r.id}>
                    <td style={td}>{r.periodLabel ?? "—"}</td>
                    <td style={td}><StatusChip status={r.status} /></td>
                    <td style={td}>{r.rawLogsImported.toLocaleString()}</td>
                    <td style={td}>{r.attendanceCreated}</td>
                    <td style={td}>{r.attendanceUpdated}</td>
                    <td style={td}>{r.unmatchedCount}</td>
                    <td style={td}>{r.flaggedForReview > 0 ? <span style={chip("var(--color-warning-fg)", "var(--color-warning-bg)")}>{r.flaggedForReview}</span> : 0}</td>
                    <td style={td}>{duration}</td>
                    <td style={td}>{fmtDateTime(r.startedAt)}</td>
                  </tr>
                );
              })}
              {runs.length === 0 && <tr><td style={{ ...td, color: "var(--color-surface-400)", textAlign: "center" }} colSpan={9}>No runs yet</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination
          meta={{ total, page, limit, totalPages: Math.max(1, Math.ceil(total / limit)) }}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
        />
      </div>
    </div>
  );
}

// ─── Shared Components ────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const map: Record<string, React.CSSProperties> = {
    DONE:       chip("var(--color-success-fg)", "var(--color-success-bg)"),
    PROCESSING: chip("var(--color-info-fg)", "var(--color-info-bg)"),
    PENDING:    chip("var(--color-warning-fg)", "var(--color-warning-bg)"),
    FAILED:     chip("var(--color-error-fg)", "var(--color-error-bg)"),
  };
  return <span style={map[status] ?? chip("var(--color-text-700)", "var(--color-surface-100)")}>{status}</span>;
}
